import './globals.css'
import App, { AppContext, AppProps } from 'next/app'
import Head from 'next/head'
import React from 'react'
import { isBrowser } from 'src/utils/index'
import { prepareHeaders } from 'src/dal/modules'
import { createMainAxiosInstance, AxiosContext } from 'src/dal/mainAxiosInstance'
import { Store } from '@reduxjs/toolkit'
import { AppState, createStore } from 'src/store/store'
import { Provider } from 'react-redux'
import initBrowserMetrics from 'nextjs-tools/metricsBrowser/index'
import { envEnum, getEnv } from 'utils/crossEnv'
import { queryClientConfig } from 'src/dal/config/queryClientConfig'
import { DehydratedState, HydrationBoundary, QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { AuthorizationContextProvider } from 'src/shared/context'

interface IProps {
  dehydratedState?: DehydratedState
  serverQueryClient?: QueryClient
  store: Store
  reduxState: AppState
  mainAxiosInstance?: any
}

// global store, used only at client
let globalClientStore: Store | undefined

const MyApp = ({
  Component,
  pageProps,
  reduxState,
  store,
  serverQueryClient,
  dehydratedState,
  mainAxiosInstance,
}: AppProps & IProps) => {
  store = store || globalClientStore || (globalClientStore = createStore(reduxState))
  const [queryClient] = React.useState(() => serverQueryClient || new QueryClient(queryClientConfig))
  const [_mainAxiosInstance] = React.useState(() => mainAxiosInstance || createMainAxiosInstance())

  initBrowserMetrics({
    env: getEnv(envEnum.NEXT_PUBLIC_ENVIRONMENT) as string,
    PRODUCT_NAME: process.env.NEXT_PUBLIC_PRODUCT_NAME as string,
    SERVICE_NAME: process.env.NEXT_PUBLIC_SERVICE_NAME as string,
  })

  return (
    <AxiosContext.Provider value={_mainAxiosInstance}>
      <Provider store={store}>
        <AuthorizationContextProvider>
          <QueryClientProvider client={queryClient}>
            <HydrationBoundary state={dehydratedState}>
              <Head>
                <meta
                  name="viewport"
                  content="width=device-width, height=device-height, initial-scale=1.0, minimum-scale=1.0"
                />
                <meta name="robots" content="noindex, nofollow" />
                <title>Сервис согласий</title>
                <link rel="stylesheet" href="https://cdn.action-mcfr.ru/ajax/libs/meyer-reset/2.0/reset.css" />
                <link rel="icon" href="/favicon.ico" type="image/x-icon" />
              </Head>
              <Component {...pageProps} />
            </HydrationBoundary>
          </QueryClientProvider>
        </AuthorizationContextProvider>
      </Provider>
    </AxiosContext.Provider>
  )
}

// SSR
MyApp.getInitialProps = async function getInitialProps(appContext: AppContext) {
  const appProps = await App.getInitialProps(appContext)

  if (isBrowser || appProps.pageProps?.statusCode === 404) {
    return appProps
  }

  const headers = appContext.ctx.req?.headers
  const mainAxiosInstance = createMainAxiosInstance()

  mainAxiosInstance.defaults.headers.common = prepareHeaders(headers)

  const getDataFromTree = (await import('../server/getDataFromTree')).default

  const data = await getDataFromTree(appContext, appProps, mainAxiosInstance)

  // @ts-ignore
  data.store.toJSON = () => undefined

  return { ...data, mainAxiosInstance }
}

export default MyApp
